'use client';

import React, { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import ServiceDiscovery from './ServiceDiscovery';
import TravelInspiration from './TravelInspiration';
import TrustSignals from './TrustSignals';

interface Destination {
  id: number;
  name: string;
  mood: string;
  image: string;
  alt: string;
  description: string;
}

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  image: string;
  alt: string;
  features: string[];
  href: string;
}

interface TravelPost {
  id: number;
  title: string;
  destination: string;
  image: string;
  alt: string;
  author: string;
  authorImage: string;
  authorAlt: string;
  date: string;
  category: string;
  readTime: string;
  excerpt: string;
}

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  review: string;
  destination: string;
  service: string;
}

interface Partnership {
  id: number;
  name: string;
  logo: string;
  alt: string;
  type: string;
}

interface Statistic {
  id: number;
  value: string;
  label: string;
  icon: string;
}

const HomepageInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockDestinations: Destination[] = [
    {
      id: 1,
      name: 'Tokyo, Japan',
      mood: 'Urban Adventure',
      image: 'https://images.unsplash.com/photo-1679016197200-9a47fabe2f2f',
      alt: 'Bustling Tokyo street at night with neon signs and crowds of people walking',
      description:
        "Experience the perfect blend of tradition and modernity in Japan's vibrant capital.",
    },
    {
      id: 2,
      name: 'Taiwan, China',
      mood: 'Traditional Culture',
      image: 'https://images.unsplash.com/photo-1618607702536-244d060a000a',
      alt: 'Traditional Chinese architecture in Taiwan',
      description: 'Discover the rich culture and history of Taiwan.',
    },
    {
      id: 3,
      name: 'Seoul, South Korea',
      mood: 'Modern City',
      image: 'https://images.unsplash.com/photo-1702911172891-d86937fcc88a',
      alt: 'Seoul skyline at night with illuminated skyscrapers and traditional Korean architecture',
      description: 'Experience the vibrant culture and modern city life of Seoul.',
    },
    {
      id: 4,
      name: 'Bangkok, Thailand',
      mood: 'Thai Culture',
      image: 'https://images.unsplash.com/photo-1708361089093-beef4c4584e7',
      alt: 'Bangkok skyline at night with illuminated skyscrapers and traditional Thai architecture',
      description: 'Experience the rich culture and history of Bangkok.',
    },
  ];

  const mockServices: Service[] = [
    {
      id: 1,
      title: 'Visa Assistance & Documentation',
      description:
        'Navigate complex visa requirements with our expert guidance. We handle all paperwork, appointments, and follow-ups to ensure your application success.',
      icon: 'DocumentTextIcon',
      image: 'https://images.unsplash.com/photo-1672826979475-816a73d3b17a',
      alt: 'Professional woman in business attire reviewing passport and visa documents at modern office desk',
      features: [
        'Complete visa application assistance',
        'Document preparation & review',
        'Embassy appointment scheduling',
        'Application status tracking',
      ],

      href: '/services',
    },
    {
      id: 2,
      title: 'Flight Booking & Management',
      description:
        'Secure the best flight deals with our extensive airline partnerships. From budget-friendly options to premium experiences, we find what fits your needs.',
      icon: 'PaperAirplaneIcon',
      image: 'https://images.unsplash.com/photo-1662653593899-f16cf5cf0b32',
      alt: 'Commercial airplane flying above white clouds against blue sky during daytime',
      features: [
        'Best price guarantee',
        'Flexible booking options',
        'Seat selection assistance',
        'Travel insurance options',
      ],

      href: '/services',
    },
    {
      id: 3,
      title: 'Complete Travel Packages',
      description:
        'Enjoy worry-free travel with our all-inclusive packages. From accommodation to activities, every detail is carefully planned and executed.',
      icon: 'MapIcon',
      image: 'https://images.unsplash.com/photo-1612257998585-c1c4cebb06f2',
      alt: 'Vintage suitcase with travel stickers next to world map and compass on wooden table',
      features: [
        'Customized itineraries',
        'Premium accommodations',
        'Local tour arrangements',
        '24/7 travel support',
      ],

      href: '/services',
    },
  ];

  const mockTravelPosts: TravelPost[] = [
    {
      id: 1,
      title: 'From Manila to Maldives: A Honeymoon Dream Come True',
      destination: 'Maldives',
      image: 'https://images.unsplash.com/photo-1549035235-66ac79dd1533',
      alt: 'Overwater bungalows with crystal clear turquoise water and tropical palm trees in Maldives',
      author: 'Maria Santos',
      authorImage: 'https://images.unsplash.com/photo-1598453171678-2d442570b54d',
      authorAlt: 'Smiling Filipino woman with long black hair wearing white summer dress',
      date: 'November 8, 2024',
      category: 'Honeymoon',
      readTime: '5 min',
      excerpt:
        'Discover how TravelEase transformed our honeymoon dreams into an unforgettable reality with seamless planning and personalized touches that made every moment magical.',
    },
    {
      id: 2,
      title: 'Solo Female Travel: My Safe Journey Through Europe',
      destination: 'Europe',
      image: 'https://images.unsplash.com/photo-1579115547021-2a160fd71cfc',
      alt: 'Young woman with backpack standing in front of Eiffel Tower in Paris during golden hour',
      author: 'Ana Reyes',
      authorImage: 'https://images.unsplash.com/photo-1665485652810-e9696b44482d',
      authorAlt: 'Confident young Filipina woman with short bob haircut smiling at camera',
      date: 'November 5, 2024',
      category: 'Solo Travel',
      readTime: '7 min',
      excerpt:
        "A detailed account of how TravelEase's comprehensive safety planning and 24/7 support made my solo European adventure both secure and empowering.",
    },
    {
      id: 3,
      title: 'Family Adventure: Creating Memories in Singapore',
      destination: 'Singapore',
      image: 'https://images.unsplash.com/photo-1602658178384-b02ef21ea180',
      alt: 'Marina Bay Sands hotel and infinity pool with Singapore city skyline at sunset',
      author: 'Carlos Mendoza',
      authorImage:
        'https://img.rocket.new/generatedImages/rocket_gen_img_18555a153-1762274244163.png',
      authorAlt:
        'Middle-aged Filipino man with glasses and friendly smile wearing casual blue shirt',
      date: 'November 3, 2024',
      category: 'Family Travel',
      readTime: '6 min',
      excerpt:
        'Learn how our family of five enjoyed a stress-free Singapore vacation with kid-friendly activities and seamless logistics handled by TravelEase.',
    },
    {
      id: 4,
      title: 'Business Travel Made Easy: My Corporate Trip to Tokyo',
      destination: 'Tokyo',
      image: 'https://images.unsplash.com/photo-1604503816506-b20032dabf9a',
      alt: 'Modern Tokyo business district with glass skyscrapers and busy professionals walking',
      author: 'Jennifer Lim',
      authorImage:
        'https://img.rocket.new/generatedImages/rocket_gen_img_1f9c04e97-1762273506521.png',
      authorAlt: 'Professional Asian businesswoman in navy blazer with confident expression',
      date: 'November 1, 2024',
      category: 'Business Travel',
      readTime: '4 min',
      excerpt:
        "Discover how TravelEase's corporate travel services streamlined my business trip to Tokyo with efficient planning and executive-level accommodations.",
    },
  ];

  const mockTestimonials: Testimonial[] = [
    {
      id: 1,
      name: 'Sarah Chen',
      location: 'Makati, Philippines',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19dc77a7e-1762274545448.png',
      alt: 'Smiling Asian woman with shoulder-length black hair wearing professional white blouse',
      rating: 5,
      review:
        'TravelEase made our dream honeymoon to Switzerland absolutely perfect. From visa processing to hotel bookings, everything was seamless. Their attention to detail and 24/7 support gave us complete peace of mind.',
      destination: 'Switzerland',
      service: 'Honeymoon Package',
    },
    {
      id: 2,
      name: 'Miguel Rodriguez',
      location: 'Cebu City, Philippines',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11531e426-1762273850653.png',
      alt: 'Friendly Filipino man with beard wearing casual gray shirt and warm smile',
      rating: 5,
      review:
        'As a first-time traveler abroad, I was nervous about the visa process. TravelEase handled everything professionally and kept me informed at every step. My trip to Canada was amazing thanks to their expertise.',
      destination: 'Canada',
      service: 'Visa Assistance',
    },
    {
      id: 3,
      name: 'Lisa Tan',
      location: 'Davao, Philippines',
      image: 'https://images.unsplash.com/photo-1661357525268-0461f3c4b815',
      alt: 'Young Filipina woman with long wavy hair smiling brightly in casual summer top',
      rating: 5,
      review:
        'The family package to Japan exceeded all our expectations. TravelEase arranged kid-friendly activities and accommodations that made traveling with children stress-free. Highly recommended!',
      destination: 'Japan',
      service: 'Family Package',
    },
  ];

  const mockPartnerships: Partnership[] = [
    {
      id: 1,
      name: 'Philippine Airlines',
      logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_17ae7ddb2-1762797467531.png',
      alt: 'Philippine Airlines logo with stylized airplane wing design',
      type: 'Airline Partner',
    },
    {
      id: 2,
      name: 'Cebu Pacific',
      logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1faac8eff-1762797464599.png',
      alt: 'Cebu Pacific airline logo with orange and blue color scheme',
      type: 'Airline Partner',
    },
    {
      id: 3,
      name: 'DOT Philippines',
      logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1dcca6ba3-1762797471541.png',
      alt: 'Department of Tourism Philippines official certification badge',
      type: 'Government License',
    },
    {
      id: 4,
      name: 'IATA',
      logo: 'https://images.unsplash.com/photo-1627129788674-bd98804f8f60',
      alt: 'International Air Transport Association membership certificate',
      type: 'Industry Certification',
    },
    {
      id: 5,
      name: 'Visa Processing Center',
      logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1efe2904b-1762797465525.png',
      alt: 'Official visa processing center accreditation logo',
      type: 'Service Partner',
    },
    {
      id: 6,
      name: 'Travel Insurance',
      logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_100b8a31f-1762797467488.png',
      alt: 'Travel insurance company partnership badge with shield icon',
      type: 'Insurance Partner',
    },
  ];

  const mockStatistics: Statistic[] = [
    {
      id: 1,
      value: '5,000+',
      label: 'Happy Travelers',
      icon: 'UserGroupIcon',
    },
    {
      id: 2,
      value: '98%',
      label: 'Visa Success Rate',
      icon: 'CheckBadgeIcon',
    },
    {
      id: 3,
      value: '50+',
      label: 'Destinations Covered',
      icon: 'GlobeAltIcon',
    },
    {
      id: 4,
      value: '24/7',
      label: 'Customer Support',
      icon: 'ClockIcon',
    },
  ];

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:347:6" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="347" data-component-end-line="354" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gradient-to-br%20from-brand-surface%20via-white%20to-brand-accent%2F10%22%7D" className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-accent/10">
        <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:348:8" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="348" data-component-end-line="353" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:349:10" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="349" data-component-end-line="349" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-screen%20bg-gray-200%22%7D" className="h-screen bg-gray-200"></div>
          <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:350:10" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="350" data-component-end-line="350" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-gray-100%20mt-8%22%7D" className="h-96 bg-gray-100 mt-8"></div>
          <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:351:10" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="351" data-component-end-line="351" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-gray-200%20mt-8%22%7D" className="h-96 bg-gray-200 mt-8"></div>
          <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:352:10" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="352" data-component-end-line="352" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-gray-100%20mt-8%22%7D" className="h-96 bg-gray-100 mt-8"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/homepage/components/HomepageInteractive.tsx:359:4" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="359" data-component-end-line="368" data-component-file="HomepageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-white%22%7D" className="min-h-screen bg-white">
      <HeroSection data-component-id="src/app/homepage/components/HomepageInteractive.tsx:360:6" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="360" data-component-end-line="360" data-component-file="HomepageInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" destinations={mockDestinations} />
      <ServiceDiscovery data-component-id="src/app/homepage/components/HomepageInteractive.tsx:361:6" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="361" data-component-end-line="361" data-component-file="HomepageInteractive.tsx" data-component-name="ServiceDiscovery" data-component-content="%7B%22elementName%22%3A%22ServiceDiscovery%22%7D" services={mockServices} />
      {/* <TravelInspiration posts={mockTravelPosts} /> */}
      <TrustSignals data-component-id="src/app/homepage/components/HomepageInteractive.tsx:363:6" data-component-path="src/app/homepage/components/HomepageInteractive.tsx" data-component-line="363" data-component-end-line="367" data-component-file="HomepageInteractive.tsx" data-component-name="TrustSignals" data-component-content="%7B%22elementName%22%3A%22TrustSignals%22%7D"
        testimonials={mockTestimonials}
        partnerships={mockPartnerships}
        statistics={mockStatistics}
      />
    </div>
  );
};

export default HomepageInteractive;
